import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  deleteSmartBillDraftAsync,
  fetchSmartBillDraftsFromServer,
  readSmartBillDrafts,
  SMART_BILL_DRAFT_HANDOFF_KEY,
} from '../../utils/smartBillDraftStorage';
import './SmartBillDrafts.css';

const formatCurrency = (value) => {
  const number = Number(value) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '—';
  }
};

const getServiceCount = (draft) => {
  const services = Array.isArray(draft?.selectedServices) ? draft.selectedServices : [];
  return services.filter((s) => (s?.displayName || s?.name || '').trim()).length;
};

const getServicesLabel = (draft) => {
  const services = Array.isArray(draft?.selectedServices) ? draft.selectedServices : [];
  const names = services
    .map((s) => (s.displayName || s.name || '').trim())
    .filter(Boolean);
  if (names.length === 0) return 'No services yet';
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
};

const SmartBillDrafts = () => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedDraftIds, setSelectedDraftIds] = useState(() => new Set());

  const refreshDrafts = useCallback(async () => {
    setLoading(true);
    setDrafts(readSmartBillDrafts());
    const rows = await fetchSmartBillDraftsFromServer();
    setDrafts(rows);
    setLoading(false);
  }, []);

  useEffect(() => {
    refreshDrafts();
  }, [refreshDrafts]);

  const filteredDrafts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return drafts;
    return drafts.filter((draft) => {
      const name = draft.customerData?.name || '';
      const phone = draft.customerData?.phone || '';
      const label = draft.label || '';
      const services = getServicesLabel(draft);
      return [name, phone, label, services].some((value) =>
        String(value).toLowerCase().includes(term)
      );
    });
  }, [drafts, searchTerm]);

  const handleResume = (draft) => {
    if (!draft) return;

    const billData = {
      customerData: draft.customerData || {},
      selectedServices:
        Array.isArray(draft.selectedServices) && draft.selectedServices.length > 0
          ? draft.selectedServices
          : undefined,
      paymentData: draft.paymentData,
      couponBillItems: draft.couponBillItems,
      sourceDraftId: draft.id,
    };

    sessionStorage.setItem(SMART_BILL_DRAFT_HANDOFF_KEY, JSON.stringify(billData));
    toast.success('Opening draft in Smart Bill…');
    navigate(`/bills/create?from=draft&id=${encodeURIComponent(draft.id)}`);
  };

  const handleDelete = async (draft) => {
    if (!draft?.id) return;
    const label = draft.customerData?.name || draft.label || 'this draft';
    if (typeof window !== 'undefined' && !window.confirm(`Delete draft for "${label}"?`)) {
      return;
    }
    const next = await deleteSmartBillDraftAsync(draft.id);
    setDrafts(next);
    toast.success('Draft deleted');
  };

  const visibleDraftIds = filteredDrafts.map((draft) => draft.id).filter(Boolean);
  const selectedVisibleCount = visibleDraftIds.filter((id) => selectedDraftIds.has(id)).length;
  const allVisibleSelected = visibleDraftIds.length > 0 && selectedVisibleCount === visibleDraftIds.length;
  const toggleDraftSelection = (id) => setSelectedDraftIds((current) => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const toggleSelectAll = () => setSelectedDraftIds((current) => { const next = new Set(current); visibleDraftIds.forEach((id) => allVisibleSelected ? next.delete(id) : next.add(id)); return next; });
  const handleBulkDelete = async () => {
    const rows = filteredDrafts.filter((draft) => selectedDraftIds.has(draft.id));
    if (!rows.length || (typeof window !== 'undefined' && !window.confirm(`Delete ${rows.length} selected draft${rows.length === 1 ? '' : 's'}? This cannot be undone.`))) return;
    await Promise.all(rows.map((draft) => deleteSmartBillDraftAsync(draft.id)));
    setDrafts(readSmartBillDrafts());
    setSelectedDraftIds((current) => { const next = new Set(current); rows.forEach((draft) => next.delete(draft.id)); return next; });
    toast.success(`${rows.length} draft${rows.length === 1 ? '' : 's'} deleted`);
  };

  return (
    <div className="smart-bill-drafts-page">
      <div className="smart-bill-drafts-header">
        <div className="header-content">
          <div>
            <h1>
              <i className="fas fa-file-alt" style={{ marginRight: 10 }} />
              Smart Bills Drafts
            </h1>
            <p>Resume unfinished Smart Bill (QR) drafts. Drafts update when you initiate payment, and are removed automatically after the bill is generated.</p>
          </div>
          <div className="header-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={refreshDrafts}
            >
              <i className="fas fa-sync-alt" />
              Refresh
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/bills/create')}
            >
              <i className="fas fa-plus" />
              New Bill
            </button>
          </div>
        </div>
      </div>

      <div className="smart-bill-drafts-filters">
        <div className="search-box">
          <i className="fas fa-search" />
          <input
            type="text"
            placeholder="Search by name, phone, or service…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {filteredDrafts.length > 0 && <div className="draft-selection-actions"><label className="select-all-drafts"><input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} /> Select all</label>{selectedVisibleCount > 0 && <button type="button" className="btn btn-bulk-delete" onClick={handleBulkDelete}><i className="fas fa-trash" /> Delete selected ({selectedVisibleCount})</button>}</div>}
        <div className="drafts-count">
          {filteredDrafts.length} draft{filteredDrafts.length === 1 ? '' : 's'}
        </div>
      </div>

      {loading ? (
        <div className="smart-bill-drafts-empty">
          <i className="fas fa-spinner fa-spin" />
          <h3>Loading drafts…</h3>
          <p>Fetching Smart Bill drafts from React and Mitra.</p>
        </div>
      ) : filteredDrafts.length === 0 ? (
        <div className="smart-bill-drafts-empty">
          <i className="fas fa-inbox" />
          <h3>No drafts found</h3>
          <p>
            {drafts.length === 0
              ? 'Drafts appear when you initiate payment or tap Save Draft on Smart Bill (QR) or Mitra. They are removed automatically after the bill is generated.'
              : 'No drafts match your search.'}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/bills/create')}
          >
            <i className="fas fa-receipt" />
            Go to Smart Bill
          </button>
        </div>
      ) : (
        <div className="smart-bill-drafts-grid">
          {filteredDrafts.map((draft) => {
            const name =
              draft.customerData?.name?.trim() ||
              draft.label ||
              'Untitled draft';
            const phone = draft.customerData?.phone || '—';
            const serviceCount = getServiceCount(draft);
            const isSelected = selectedDraftIds.has(draft.id);

            return (
              <div key={draft.id} className={`draft-card ${isSelected ? 'is-selected' : ''}`}>
                <label className="draft-select"><input type="checkbox" checked={isSelected} onChange={() => toggleDraftSelection(draft.id)} aria-label={`Select draft for ${name}`} /></label>
                <div className="draft-card-top">
                  <div className="draft-avatar">
                    <i className="fas fa-user" />
                  </div>
                  <div className="draft-meta">
                    <h3>{name}</h3>
                    <p>
                      <i className="fas fa-phone" /> {phone}
                    </p>
                    <p className="draft-saved-at">
                      <i className="fas fa-clock" /> {formatDateTime(draft.savedAt)}
                    </p>
                  </div>
                  <div className="draft-amount">
                    {formatCurrency(draft.totalAmount)}
                  </div>
                </div>

                <div className="draft-services">
                  <span className="service-chip">
                    {serviceCount} service{serviceCount === 1 ? '' : 's'}
                  </span>
                  {draft.source ? (
                    <span className="service-chip">
                      {String(draft.source).toLowerCase() === 'mitra'
                        ? 'Mitra'
                        : 'React'}
                    </span>
                  ) : null}
                  <span className="services-text">{getServicesLabel(draft)}</span>
                </div>

                {draft.customerData?.remarks ? (
                  <p className="draft-remarks">
                    <i className="fas fa-comment" /> {draft.customerData.remarks}
                  </p>
                ) : null}

                <div className="draft-card-actions">
                  <button
                    type="button"
                    className="btn btn-resume"
                    onClick={() => handleResume(draft)}
                  >
                    <i className="fas fa-play" />
                    Resume
                  </button>
                  <button
                    type="button"
                    className="btn btn-delete"
                    onClick={() => handleDelete(draft)}
                  >
                    <i className="fas fa-trash" />
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SmartBillDrafts;
